import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { messageErrorConfirmDialog, messageSuccessConfirmDialog, PropsMessageDialog } from "../../../components/message-dialog";
import { OralModel } from "../../../models/Oral.model";
import { OralViewModel } from "../../../models/OralView.model";
import { getAllOralActive, saveOralActive, getOralActive } from "../../../services/oralService";
import { handlePromise } from "../../../utils/handlePromisse";
import { unityOptions } from "../../../models/unity-options.model";

const PAGE_SIZE = 10;

const productViewInitial: OralViewModel = {
    id: undefined,
    enabled: true,
    description: "",
    unity: unityOptions[0]?.value,
    classifications: [],
    userClassification: [],
    name: "",
    mechanism: "",
    min: "",
    max: "",
    source: "",
    period: "",
};

const productSchema = Yup.object().shape({
    description: Yup.string().required("Este campo é obrigatório"),
    classifications: Yup.mixed().test({
        message: "Este campo é obrigatório",
        test: (val) => val.length > 0,
    }),
    name: Yup.string().required("Este campo é obrigatório"),
    mechanism: Yup.string().required("Este campo é obrigatório"),
    source: Yup.string().required("Este campo é obrigatório"),
    unity: Yup.string().required("Este campo é obrigatório"),
    min: Yup.string().required("Este campo é obrigatório"),
    max: Yup.string().required("Este campo é obrigatório"),
    period: Yup.string().required("Este campo é obrigatório"),
});

/** Estado, formik, fetch e handlers da administração de ativos orais. */
export function useManageOral() {
    const formik = useFormik({
        initialValues: productViewInitial,
        validationSchema: productSchema,
        onSubmit: () => {
            registerProduct();
        },
    });

    const [classes, setClasses] = useState<string>("");
    const [selectedClasses, setSelectedClasses] = useState<string[]>([]);
    const [activeDisabled, setActiveDisabled] = React.useState<boolean>(false);
    const [allActives, setAllActives] = useState<OralModel[]>([]);
    const [filterArray, setFilterArray] = useState<string[]>([]);
    const [formOpen, setFormOpen] = useState(false);
    const [open, setOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = React.useState<OralModel>({} as OralModel);
    const [openBackdrop, setOpenBackdrop] = useState(false);
    const [productViewModel, setProductViewModel] = React.useState<OralViewModel>(productViewInitial);
    const [btnSaveDisabled, setBtnSaveDisabled] = React.useState<boolean>(false);
    const [dialogModel, setDialogModel] = React.useState<PropsMessageDialog>({
        onConfirm: () => {
            dialogModel.open = false;
            setDialogModel({ ...dialogModel });
        },
        open: false,
    } as PropsMessageDialog);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedPeriod, setSelectedPeriod] = useState("ambos");
    const [selectedClassification, setSelectedClassification] = useState("");
    const [page, setPage] = useState(1);
    const [admin] = useState(true);

    const fetchOralActives = useCallback(async () => {
        const [result, error] = await handlePromise(getAllOralActive(admin));
        setAllActives(result.data.data as OralModel[]);
    }, [admin]);

    useEffect(() => {
        fetchOralActives();
        autoCompletefilter();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchOralActives]);

    const autoCompletefilter = () => {
        const newActiveArray: string[] = [];

        allActives.forEach((active) => {
            active.classifications.forEach((classification) => {
                newActiveArray.push(classification);
            });
        });
        const uniqueActives = Array.from(new Set(newActiveArray));

        setFilterArray(uniqueActives);
    };

    const saveFilter = useCallback(
        (newValue: string[]) => {
            setSelectedClasses([...newValue]);
            productViewModel.classifications = [];
            newValue.forEach((classification: string) => {
                productViewModel.classifications.push(classification);
            });
        },
        [productViewModel]
    );

    const resetForm = () => {
        setSelectedClasses([]);
        setProductViewModel({ ...productViewInitial });
        setActiveDisabled(false);
    };

    const closeForm = () => {
        resetForm();
        setFormOpen(false);
    };

    const registerProduct = () => {
        setBtnSaveDisabled(true);
        setOpenBackdrop(true);
        const product: OralModel = {
            ...productViewModel,
        };
        saveOralActive(product)
            .then(() => {
                const message = product.id == null ? "Ativo cadastrado com sucesso!" : "Ativo alterado com sucesso";
                setDialogModel({ ...messageSuccessConfirmDialog(dialogModel.onConfirm, message) });
                resetForm();
                setFormOpen(false);
                fetchOralActives();
            })
            .catch(() => setDialogModel({ ...messageErrorConfirmDialog(dialogModel.onConfirm) }))
            .finally(() => {
                setOpenBackdrop(false);
                setBtnSaveDisabled(false);
            });
    };

    const updateFormikValues = (formikInstance: any, values: any) => {
        Object.entries(values).map(([key, val]) => (formikInstance.values[key] = val));
    };

    const handleClickViewActive = (product: OralModel) => {
        setSelectedProduct(product as OralModel);
        setOpen(true);
    };

    const handleClickEnable = useCallback(
        async (active: OralModel) => {
            setOpenBackdrop(true);
            if (active.enabled == true) {
                active.enabled = false;
                await saveOralActive(active);
            } else if (active.enabled == false) {
                active.enabled = true;
                await saveOralActive(active);
            }
            await fetchOralActives();
            setOpenBackdrop(false);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const handleClickEditActive = async (product: OralModel) => {
        setOpenBackdrop(true);
        if (product.id) {
            Promise.all([getOralActive(product.id)]).then((response) => {
                if (response[0].status !== 200) {
                    setDialogModel({ ...messageErrorConfirmDialog(dialogModel.onConfirm) });
                    return;
                }
                saveFilter(product.classifications);
                productViewModel.name = product.name;
                productViewModel.source = product.source;
                productViewModel.period = product.period;
                productViewModel.mechanism = product.mechanism;
                productViewModel.description = product.description;
                productViewModel.min = product.min;
                productViewModel.max = product.max;
                productViewModel.id = product.id;

                setActiveDisabled(true);
                setProductViewModel({ ...productViewModel });
                setOpenBackdrop(false);
                setFormOpen(true);
            });
        }
    };

    const handleAddClass = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === "Enter" && classes && !selectedClasses.includes(classes)) {
                event.preventDefault();

                const newClassification = classes.split(/,|\s+|\n/);
                if (newClassification.length > 0) {
                    const filtered = newClassification.filter((e) => !selectedClasses.includes(e.trim()));
                    productViewModel.classifications.push(filtered[0]);
                    setSelectedClasses((oldClassification) => [...oldClassification, ...filtered]);
                } else {
                    setSelectedClasses((oldClassification) => [...oldClassification, classes.trim()]);
                    productViewModel.classifications.push(classes.trim());
                }

                setClasses("");
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedClasses, classes]
    );

    const handleRemoveClass = useCallback((email: string) => {
        setSelectedClasses((emails) => emails.filter((e) => e !== email));
    }, []);

    useEffect(() => {
        updateFormikValues(formik, productViewModel);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productViewModel]);

    // ── derived ──────────────────────────────────────────────────────────

    const distinctClassifications = useMemo(() => {
        const set = new Set<string>();
        allActives.forEach((active) => {
            active.classifications.forEach((c) => set.add(c));
        });
        return Array.from(set).sort();
    }, [allActives]);

    const filteredActives = useMemo(() => {
        return allActives
            .filter((row) => row.name.toLowerCase().includes(searchTerm.toLowerCase()))
            .filter((row) => (selectedPeriod === "ambos" ? true : row.period.toLowerCase() === selectedPeriod))
            .filter((row) => (selectedClassification === "" ? true : row.classifications.includes(selectedClassification)));
    }, [allActives, searchTerm, selectedPeriod, selectedClassification]);

    const pagedActives = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return filteredActives.slice(start, start + PAGE_SIZE);
    }, [filteredActives, page]);

    const openCreate = () => {
        resetForm();
        setFormOpen(true);
    };

    return {
        formik,
        // form state
        formOpen,
        closeForm,
        openCreate,
        productViewModel,
        setProductViewModel,
        productViewInitial,
        productSchema,
        btnSaveDisabled,
        selectedClasses,
        filterArray,
        setClasses,
        saveFilter,
        handleAddClass,
        handleRemoveClass,
        autoCompletefilter,
        // detail / dialogs
        open,
        setOpen,
        selectedProduct,
        dialogModel,
        openBackdrop,
        // list + filters
        allActives,
        searchTerm,
        setSearchTerm,
        selectedPeriod,
        setSelectedPeriod,
        selectedClassification,
        setSelectedClassification,
        page,
        setPage,
        pageSize: PAGE_SIZE,
        distinctClassifications,
        filteredActives,
        pagedActives,
        // row actions
        handleClickEnable,
        handleClickEditActive,
        handleClickViewActive,
    };
}
